import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Search, ShieldCheck, Wrench, Loader2 } from "lucide-react";
import { HERO_IMAGE, resolvePopularImage } from "../mock";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { fetchCatalogStats, fetchMegaMenu, fetchProducts } from "../lib/api";

export default function Home() {
  const { data: megaMenu = [] } = useQuery({
    queryKey: ["mega-menu"],
    queryFn: fetchMegaMenu,
  });

  const { data: stats } = useQuery({
    queryKey: ["catalog-stats"],
    queryFn: fetchCatalogStats,
  });

  const homeSection = megaMenu.find((s) => s.slug === "home-appliances-hvac");
  const popularSection = megaMenu.find((s) => s.slug === "popular");

  const { data: featuredData, isLoading: loadingFeatured } = useQuery({
    queryKey: ["featured-products"],
    queryFn: () => fetchProducts({ menu: "refrigerator-water-filters", limit: 10 }),
  });

  const { data: recentData, isLoading: loadingRecent } = useQuery({
    queryKey: ["recent-products"],
    queryFn: () => fetchProducts({ limit: 8, sort: "name" }),
  });

  const categories = homeSection?.items?.filter((i) => !i.is_link_only) || [];
  const popular = popularSection?.items || [];
  const featured = featuredData?.items || [];
  const recent = recentData?.items || [];

  return (
    <div>
      <section className="relative bg-gradient-to-br from-slate-50 to-emerald-50/40 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-rp-green bg-white border border-border rounded-full px-3 py-1.5">
              <Wrench className="h-3.5 w-3.5" />
              Appliance Parts · Canada
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
              Genuine replacement parts,
              <span className="text-rp-green"> from A to Z.</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-xl">
              Refrigerators, washers, dryers, dishwashers, ranges and more.
              {stats?.products ? ` Over ${stats.products.toLocaleString()} parts in stock.` : ""}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/boutique">
                <Button className="h-12 px-6 rounded-full bg-rp-green hover:bg-rp-green-dark text-base gap-2">
                  Shop all parts <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/boutique?menu=refrigerator-parts">
                <Button variant="outline" className="h-12 px-6 rounded-full text-base border-slate-300">
                  Refrigerator parts
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-rp-green" /> OEM & compatible parts
              </span>
              <span className="flex items-center gap-1.5">
                <Search className="h-4 w-4 text-rp-green" /> Search by model #
              </span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/15 border border-white">
              <img
                src={HERO_IMAGE}
                alt="Appliance repair parts"
                className="w-full h-[340px] md:h-[420px] object-cover"
              />
            </div>
            {stats && (
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-border px-5 py-4 hidden sm:block">
                <p className="text-2xl font-extrabold text-rp-green">{stats.products.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">parts in catalog</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Home Appliances / HVAC</h2>
            <p className="text-muted-foreground mt-1">Browse by appliance type.</p>
          </div>
          <Link to="/boutique" className="text-sm font-semibold text-rp-green hover:underline whitespace-nowrap">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/boutique?menu=${c.slug}`}
              className="group card-lift bg-white border border-border rounded-xl overflow-hidden p-5"
            >
              <h3 className="font-semibold text-slate-900 group-hover:text-rp-green">{c.name}</h3>
              {c.product_count > 0 && (
                <p className="text-sm text-muted-foreground mt-1">{c.product_count.toLocaleString()} parts</p>
              )}
              <ArrowRight className="h-4 w-4 text-rp-green mt-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </section>

      {popular.length > 0 && (
        <section className="bg-[#f5f5f5] border-y border-border">
          <div className="max-w-[1400px] mx-auto px-4 py-14">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Popular</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {popular.map((item) => {
                const imageSrc = resolvePopularImage(item);
                return (
                  <Link
                    key={item.slug}
                    to={`/boutique?menu=${item.slug}`}
                    className="group bg-white border border-slate-200 rounded-xl overflow-hidden p-4 hover:shadow-md transition-shadow"
                  >
                    {imageSrc && (
                      <div className="aspect-[4/3] flex items-center justify-center mb-3 bg-slate-50 rounded-lg">
                        <img
                          src={imageSrc}
                          alt={item.name}
                          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <span className="font-medium text-slate-800 group-hover:text-rp-green group-hover:underline">
                      {item.name}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-4 py-14">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Refrigerator Water Filters</h2>
            <Link to="/boutique?menu=refrigerator-water-filters" className="text-sm font-semibold text-rp-green hover:underline">
              View more →
            </Link>
          </div>
          {loadingFeatured ? (
            <div className="flex justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {featured.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-[1400px] mx-auto px-4 py-14">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Browse catalog</h2>
          {loadingRecent ? (
            <div className="flex justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {recent.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 py-16">
        <div className="rounded-2xl bg-rp-nav text-white px-8 py-12 md:py-16 text-center relative overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold">Need help finding a part?</h2>
            <p className="mt-3 text-white/80 text-lg">
              Search by part number or model, or contact our team for assistance.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/boutique">
                <Button className="h-12 px-6 rounded-full bg-rp-green hover:bg-rp-green-dark text-base">
                  Shop all parts
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="h-12 px-6 rounded-full text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-rp-dark"
                >
                  Contact us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
